import React, { useCallback, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Tooltip,
} from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const ZoomableImage = ({
  src,
  previewHeight = 220,
  previewFit = "cover",
  minZoom = 1,
  maxZoom = 10,
  initialZoom = 1,
  dialogMaxWidth = "xl",
  sx = {},
}) => {
  const [open, setOpen] = useState(false);
  const [scale, setScale] = useState(initialZoom);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panAnchorRef = useRef({ x: 0, y: 0 });

  const handleOpen = useCallback(() => {
    setScale(initialZoom);
    setPosition({ x: 0, y: 0 });
    setOpen(true);
  }, [initialZoom]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setIsPanning(false);
  }, []);

  const applyZoom = useCallback(
    (delta) => {
      setScale((current) => {
        const next = clamp(current + delta, minZoom, maxZoom);
        if (next === minZoom) {
          setPosition({ x: 0, y: 0 });
        }
        return next;
      });
    },
    [maxZoom, minZoom]
  );

  const resetTransform = useCallback(() => {
    setScale(initialZoom);
    setPosition({ x: 0, y: 0 });
  }, [initialZoom]);

  const handleWheel = useCallback(
    (event) => {
      event.preventDefault();
      const delta = event.deltaY < 0 ? 0.15 : -0.15;
      applyZoom(delta);
    },
    [applyZoom]
  );

  const handleMouseDown = useCallback(
    (event) => {
      if (scale <= 1) return;
      setIsPanning(true);
      panAnchorRef.current = {
        x: event.clientX - position.x,
        y: event.clientY - position.y,
      };
    },
    [position.x, position.y, scale]
  );

  const handleMouseMove = useCallback(
    (event) => {
      if (!isPanning) return;
      setPosition({
        x: event.clientX - panAnchorRef.current.x,
        y: event.clientY - panAnchorRef.current.y,
      });
    },
    [isPanning]
  );

  const stopPanning = useCallback(() => setIsPanning(false), []);

  const cursorStyle = useMemo(() => {
    if (scale <= 1) return "zoom-in";
    return isPanning ? "grabbing" : "grab";
  }, [isPanning, scale]);

  return (
    <>
      <Box
        sx={{
          position: "relative",
          height: previewHeight,
          width: "100%",
          borderRadius: "12px",
          overflow: "hidden",
          backgroundColor: "#0f172a",
          cursor: "zoom-in",
          "&:hover img": { transform: "scale(1.03)" },
          ...sx,
        }}
        onClick={handleOpen}
      >
        <Box
          component="img"
          src={src}
          draggable={false}
          sx={{
            height: "100%",
            width: "100%",
            objectFit: previewFit,
            transition: "transform 160ms ease-out",
          }}
        />
      </Box>

      <Dialog
        open={open}
        fullWidth
        maxWidth={dialogMaxWidth}
        onClose={handleClose}
      >
        <DialogContent
          sx={{
            position: "relative",
            backgroundColor: "#0b1224",
            px: 0,
            pb: 0,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              zIndex: 2,
              backgroundColor: "rgba(0,0,0,0.65)",
              borderRadius: "14px",
              boxShadow: "0 12px 32px rgba(0,0,0,0.45)",
              backdropFilter: "blur(8px)",
              px: 0.75,
              py: 0.5,
            }}
          >
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Увеличить">
                <span>
                  <IconButton
                    size="small"
                    aria-label="zoom-in"
                    onClick={() => applyZoom(0.25)}
                    disabled={scale >= maxZoom}
                    sx={{
                      color: "#fff",
                      "&.Mui-disabled": { color: "rgba(255,255,255,0.35)" },
                      "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" },
                    }}
                  >
                    <ZoomInIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Уменьшить">
                <span>
                  <IconButton
                    size="small"
                    aria-label="zoom-out"
                    onClick={() => applyZoom(-0.25)}
                    disabled={scale <= minZoom}
                    sx={{
                      color: "#fff",
                      "&.Mui-disabled": { color: "rgba(255,255,255,0.35)" },
                      "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" },
                    }}
                  >
                    <ZoomOutIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Сбросить">
                <IconButton
                  size="small"
                  aria-label="reset"
                  onClick={resetTransform}
                  sx={{
                    color: "#fff",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" },
                  }}
                >
                  <RestartAltIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Закрыть">
                <IconButton
                  size="small"
                  aria-label="close"
                  onClick={handleClose}
                  sx={{
                    color: "#fff",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>

          <Box
            sx={{
              height: "75vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              position: "relative",
              userSelect: "none",
            }}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={stopPanning}
            onMouseLeave={stopPanning}
          >
            <Box
              component="img"
              src={src}
              draggable={false}
              sx={{
                maxHeight: "90%",
                maxWidth: "90%",
                transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                transition: isPanning ? "none" : "transform 120ms ease-out",
                cursor: cursorStyle,
                boxShadow: "0 10px 40px rgba(0,0,0,0.45)",
                borderRadius: "12px",
                backgroundColor: "#0b1224",
              }}
            />
          </Box>

        </DialogContent>
      </Dialog>
    </>
  );
};

ZoomableImage.propTypes = {
  src: PropTypes.string.isRequired,
  previewHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  previewFit: PropTypes.oneOf(["cover", "contain", "fill", "none", "scale-down"]),
  minZoom: PropTypes.number,
  maxZoom: PropTypes.number,
  initialZoom: PropTypes.number,
  dialogMaxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  sx: PropTypes.object,
};

export default ZoomableImage;
